<template>
  <div class="search">
    <h1>Search Results for {{ decodeURIComponent(searchQuery).replace(/['"]/g, '') }}</h1>
    
    <div v-if="initialLoading && allProducts.length === 0" class="loading">
      <div class="spinner"></div>
      <strong>Searching products...</strong>
      <div class="wait-time"><strong>Please wait 10-20 seconds</strong></div>
    </div>
    
    <div v-if="error" class="error">
      {{ error }}
      <button @click="performSearch" class="retry-button">Try Again</button>
    </div>
    
    <div v-if="!initialLoading && !error && allProducts.length > 0" class="results">
      <div class="filters-container">
        <div class="sort-container">
          <select v-model="sortOption" @change="handleSort" class="sort-select">
            <option value="default">Sort by: </option>
            <option value="desc">Sort by: High To Low</option>
            <option value="asc">Sort by: Low To High</option>
          </select>
        </div>
        
        <div class="price-filter">
          <div class="price-inputs">
            <input 
              type="number" 
              v-model="minPrice" 
              placeholder="Min €"
              min="0"
              class="price-input"
              @input="logPriceInput"
            >
            <input 
              type="number" 
              v-model="maxPrice" 
              placeholder="Max €"
              min="0"
              class="price-input"
              @input="logPriceInput"
            >
            <button 
              class="apply-btn"
              @click="applyPriceFilter"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
      <div class="price-banner">
        To find authentic products, use a price range that matches the brand and always check the seller’s profile.
      </div>
      
      <div v-if="!filteredProducts.length && !loadingMorePages && streamComplete" class="no-results">
        No products found for "{{ searchQuery }}"
      </div>
      
      <div v-else class="products">
        <div 
          v-for="(product, index) in filteredProducts" 
          :key="`${product.text}-${product.sellerName}-${index}`" 
          class="product-card"
          @click="goToProductDetail(product)"
        >
          <div class="product-image">
            <img :src="product.image" :alt="cleanedDescription(product.text)" @error="handleImageError" loading="lazy" />
          </div>
          <div class="product-details">
            <h3 class="product-text-title">{{ cleanedDescription(product.text) }}</h3>
            <div class="product-price-row">
              <div v-if="product.originalPrice" class="price-container">
                <p class="product-price original-price" style="text-decoration: line-through; color: black; font-size: 0.9em; margin-right: 5px;">{{ product.originalPrice }}</p>
                <p class="product-price new-price" style="color: red; font-weight: bold;">{{ product.price }}</p>
              </div>
              <p v-else class="product-price">{{ product.price }}</p>
              <button class="favourite-btn" @click.stop.prevent="toggleFavourite(product)">
                <i
                  v-if="isFavourited(product)"
                  class="fas fa-heart favourite-heart is-active"
                  :class="{ popping: poppingFavouriteUrl === getFavouriteUrl(product) }"
                ></i>
                <i v-else class="far fa-heart favourite-heart"></i>
              </button>
            </div>
            <div class="seller-details">
              <div class="seller-info">
                <img 
                  :src="product.sellerImage" 
                  :alt="product.sellerName"
                  @error="handleSellerImageError"
                  class="seller-image"
                  loading="lazy"
                />
                <span class="seller-name">{{ product.sellerName }}</span>
              </div>
              <span class="reviews-count" v-if="product.sellerReviews">{{ cleanedDescription(product.sellerReviews) }}</span>
            </div>
          </div>
          <div class="product-badge" v-if="product.badge">{{ product.badge }}</div>
        </div>
      </div>
      
      <div v-if="loadingMorePages" class="full-page-loader">
        <div class="spinner"></div>
        <strong>Loading more products...</strong>
        <div class="wait-time"><strong>Please wait 10-20 seconds</strong></div>
      </div>

      <div class="pagination" v-if="maxPages > 1">
        <button 
          class="pagination-btn" 
          :disabled="currentPage === 1"
          @click="goToPage(1)"
        >
          First
        </button>
        
        <button 
          class="pagination-btn" 
          :disabled="currentPage === 1"
          @click="goToPage(currentPage - 1)"
        >
          Previous
        </button>
        
        <input 
          type="number" 
          v-model.number="pageInput" 
          min="1" 
          :max="maxPages" 
          class="page-input"
        />
        <button 
          class="pagination-btn go-btn"
          @click="goToPage(pageInput)"
        >
          Go
        </button>

        <button 
          class="pagination-btn" 
          :disabled="currentPage === maxPages"
          @click="goToPage(currentPage + 1)"
        >
          Next
        </button>

        <button 
          class="pagination-btn" 
          :disabled="currentPage === maxPages"
          @click="goToPage(maxPages)"
        >
          Last
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import tokenService from '../services/tokenService';
import favouritesService from '../services/favouritesService';
import productNavigation from '../services/productNavigation';

export default {
  name: 'Search',
  props: {
    query: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchQuery: '',
      allProducts: [],
      initialLoading: true,
      loadingMorePages: false,
      currentPage: 1,
      maxPages: 1,
      pageInput: 1,
      error: null,
      sortOption: 'default',
      minPrice: null,
      maxPrice: '',
      originalProducts: [],
      streamComplete: false,
      searchAbortController: null,
      isInitialized: false,
      currentSearchQuery: null,
      favouritesMap: {},
      poppingFavouriteUrl: null,
    }
  },
  async created() {
    // Initialize HTTP client
    try {
      // Try to get initial API token
      await tokenService.getApiToken();
      this.isInitialized = true;
      
      // If we have a query on initial load, perform search
      if (this.$route.query.q) {
        this.searchQuery = this.$route.query.q;
        await this.handleSearch();
      } else {
        this.initialLoading = false;
      }

      // Load favourites (session-based). Non-blocking for search.
      this.loadFavourites();
      this.$root.$on('favourites-changed', this.loadFavourites);
    } catch (error) {
      console.error('Failed to initialize search:', error);
      this.error = 'Failed to initialize search. Please try again.';
      this.initialLoading = false;
    }
  },
  beforeDestroy() {
    this.$root.$off('favourites-changed', this.loadFavourites);
  },
  computed: {
    filteredProducts() {
      let products = [...this.allProducts];
      
      // Filter out products with price "€ 0"
      products = products.filter(product => {
        const price = product.price.trim();
        return price !== "€ 0" && price !== "0 €" && price !== "€0";
      });
      
      // Apply sorting
      products.sort((a, b) => {
        const priceA = parseFloat(a.price.replace(/[^0-9.-]+/g, ''));
        const priceB = parseFloat(b.price.replace(/[^0-9.-]+/g, ''));
        
        if (this.sortOption === 'desc') {
          return priceB - priceA; // High to Low
        } else if (this.sortOption === 'asc') {
          return priceA - priceB; // Low to High
        } else {
          // Default sorting - maintain original order or any other default logic
          return 0; // Keep original order
        }
      });
      
      return products;
    },

    cleanedDescription() {
      const numberWords = '(?:\\d+|one|two|three|four|five|six|seven|eight|nine|ten|twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety|hundred|thousand)';
      
      return (text) => {
        if (!text) return '';
        
        return text
          .replace(/\bExpand\b/g, '')
          // Remove number words or digits followed by yuan
          .replace(new RegExp(`\\b${numberWords}\\s*yuan\\b`, 'gi'), '')
          // Remove price ranges with number words or digits
          .replace(new RegExp(`\\b${numberWords}\\s*(?:yuan|元)\\s*[,，]?\\s*(?:(?:now|price|at)\\s+)?${numberWords}\\s*(?:yuan|元)\\b`, 'gi'), '')
          // Remove free shipping phrases
          .replace(/\b(?:free\s+shipping|shipping\s+free|free\s+delivery|delivery\s+free)\b/gi, '')
          // Remove remote areas exceptions
          .replace(/\b(?:not\s+for|except\s+for|excluding|except)(?:\s+the)?\s+remote\s+areas?\b/gi, '')
          .replace(/\bTaobao\b/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      };
    },
    displayedPageNumbers() {
      const current = this.currentPage;
      const max = this.maxPages;
      const delta = 2;
      
      let range = [];
      
      // Always include first page
      range.push(1);
      
      for (let i = Math.max(2, current - delta); i <= Math.min(max - 1, current + delta); i++) {
        range.push(i);
      }
      
      // Always include last page
      if (max > 1) {
        range.push(max);
      }
      
      // Add ellipses where needed
      let withEllipsis = [];
      let l;
      
      for (let i of range) {
        if (l) {
          if (i - l === 2) {
            withEllipsis.push(l + 1);
          } else if (i - l !== 1) {
            withEllipsis.push('...');
          }
        }
        withEllipsis.push(i);
        l = i;
      }
      
      return withEllipsis;
    }
  },
  watch: {
    $route: {
      immediate: true,
      async handler(newRoute, oldRoute) {
        // Only proceed if component is initialized
        if (!this.isInitialized) {
          return;
        }

        // Stop any ongoing search if we're not on the search page or missing query
        if (!newRoute.path.startsWith('/search') || !newRoute.query.q) {
          if (this.searchAbortController) {
            this.searchAbortController.abort();
            this.searchAbortController = null;
          }
          this.resetSearch(false);
          this.initialLoading = false;
          return;
        }

        // Check if the search query has changed
        if (newRoute.query.q !== oldRoute?.query?.q) {
          this.searchQuery = newRoute.query.q;
          await this.handleSearch();
        } else if (newRoute.query.page && oldRoute?.query?.page && newRoute.query.page !== oldRoute.query.page) {
          // If only page changed
          await this.goToPage(parseInt(newRoute.query.page));
        } else if (newRoute.query.page && !this.allProducts.length && !this.initialLoading) {
          // Coming back to a page, reload
          await this.handleSearch(true);
        }
      }
    }
  },
  methods: {
    getFavouriteUrl(product) {
      return favouritesService.getProductUrlFromProduct(product);
    },

    isFavourited(product) {
      const url = this.getFavouriteUrl(product);
      return !!(url && this.favouritesMap && this.favouritesMap[url]);
    },

    async loadFavourites() {
      try {
        const list = await favouritesService.listFavourites();
        const nextMap = {};
        for (const fav of list) {
          if (fav && fav.url) nextMap[fav.url] = true;
        }
        this.favouritesMap = nextMap;
      } catch (err) {
        // Ignore if not logged in
        if (err && err.response && err.response.status === 401) return;
        console.warn('Failed to load favourites', err);
      }
    },

    async toggleFavourite(product) {
      const url = this.getFavouriteUrl(product);
      if (!url) {
        this.$root.$emit('show-toast', { message: 'Unable to favourite: missing product URL.', type: 'error' });
        return;
      }

      const currentlyFav = !!this.favouritesMap[url];
      try {
        this.poppingFavouriteUrl = currentlyFav ? null : url;
        if (currentlyFav) {
          await favouritesService.removeFavourite(url);
          this.$delete(this.favouritesMap, url);
        } else {
          await favouritesService.addFavourite(url);
          this.$set(this.favouritesMap, url, true);
        }
        setTimeout(() => {
          if (this.poppingFavouriteUrl === url) this.poppingFavouriteUrl = null;
        }, 220);
      } catch (err) {
        if (err && err.response && err.response.status === 401) {
          if (!currentlyFav) favouritesService.setPendingFavourite(url);
          this.$root.$emit('open-login-modal');
          return;
        }
        this.$root.$emit('show-toast', {
          message: err?.response?.data?.error || err?.message || 'Failed to update favourite.',
          type: 'error'
        });
      }
    },
    async handleSearch(preservePage = false) {
      try {
        // Check if we're on the search page with strict path check
        if (!this.$route.path.startsWith('/search')) {
          console.log('Not on search page, stopping search');
          if (this.searchAbortController) {
            this.searchAbortController.abort();
            this.searchAbortController = null;
          }
          this.resetSearch();
          this.initialLoading = false;
          return;
        }

        // Check if we have a valid search query
        if (!this.searchQuery) {
          console.log('No search query, stopping search');
          if (this.searchAbortController) {
            this.searchAbortController.abort();
            this.searchAbortController = null;
          }
          this.resetSearch(preservePage);
          this.initialLoading = false;
          return;
        }

        // Cancel any ongoing search
        if (this.searchAbortController) {
          this.searchAbortController.abort();
          this.searchAbortController = null;
          // Add a small delay to ensure cleanup
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // Set current search query
        //this.currentSearchQuery = this.searchQuery;
        this.currentSearchQuery = decodeURIComponent(this.searchQuery);
        
        // Create new abort controller for this search
        this.searchAbortController = new AbortController();
        
        // Reset search state
        this.resetSearch(preservePage);
        
        // Clear all products array
        this.allProducts = [];
        this.initialLoading = true;
        
        // Ensure we have a valid API token before searching
        try {
          await tokenService.getApiToken();
        } catch (error) {
          console.error('Failed to get token:', error);
          this.error = 'Failed to authenticate search. Please try again.';
          this.initialLoading = false;
          return;
        }
        
        // Perform the search
        await this.performSearch(preservePage);
      } catch (error) {
        // Only show error if it's not an abort error
        if (error.name !== 'AbortError') {
          console.error('Error in handleSearch:', error);
          this.error = 'An error occurred while searching. Please try again.';
          this.initialLoading = false;
        }
      }
    },
    
    async getAuthToken() {
      // Use the token service to get a valid API token
      return await tokenService.getApiToken();
    },
    
    async performSearch(preservePage = false) {
      // Strict check for search page and query
      if (!this.$route.path.startsWith('/search') || !this.$route.query.q) {
        console.log('Not on search page or missing query, stopping search');
        if (this.searchAbortController) {
          this.searchAbortController.abort();
          this.searchAbortController = null;
        }
        this.resetSearch();
        this.initialLoading = false;
        return;
      }

      if (!this.searchQuery) {
        this.resetSearch();
        this.initialLoading = false;
        return;
      }

      const searchQueryAtStart = this.currentSearchQuery;

      try {
        // Show loading state
        if (this.currentPage > 1) {
          this.loadingMorePages = true;
        }

        try {
          const apiUrl = `/api/search/page?keyword=${this.searchQuery.trim()}&page=${this.currentPage}&priceFilter=${this.sortOption}${this.minPrice ? `&minPrice=${this.minPrice}` : ''}${this.maxPrice ? `&maxPrice=${this.maxPrice}` : ''}`;
          
          let retryCount = 0;
          const maxRetries = 3;
          
          while (retryCount < maxRetries) {
            try {
              const response = await tokenService.executeAuthenticatedRequest(async (token) => {
                // Check if search was cancelled or if search query changed
                if (!this.searchAbortController || 
                    this.searchAbortController.signal.aborted || 
                    this.currentSearchQuery !== searchQueryAtStart) {
                  throw new DOMException('Search aborted', 'AbortError');
                }
                
                return await this.$http.post(apiUrl, {}, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  },
                  signal: this.searchAbortController.signal
                });
              });
              
              // Check again if search query changed before processing response
              if (this.currentSearchQuery !== searchQueryAtStart) {
                console.log('Search query changed, discarding results');
                return;
              }
              
              // Process response
              if (response.data && Array.isArray(response.data.products)) {
                const processedProducts = response.data.products.map(product => ({
                  ...product,
                  text: product.text || 'No title',
                  price: product.price || 'Price not available',
                  image: this.sanitizeImageUrl(product.image),
                  sellerName: product.sellerName || 'Seller',
                  sellerImage: this.sanitizeImageUrl(product.sellerImage) || this.getDefaultSellerImage(),
                  sellerReviews: product.sellerReviews || null,
                  badge: product.badge || null,
                  productLink: product.productLink || null
                }));
                
                // After first page, we can show content
                if (this.currentPage === 0) {
                  this.initialLoading = false;
                }
                
                // Add products and sort, using deduplication
                this.allProducts = this.deduplicateProducts([...this.allProducts, ...processedProducts]);
                this.sortProducts();
                
                // Update current page and check if there are more pages
                this.currentPage = response.data.page;
                this.maxPages = response.data.totalPages;
                this.streamComplete = !response.data.hasMorePages;
                this.error = null; // clear error on success
                
                if (!response.data.hasMorePages) {
                  this.loadingMorePages = false;
                  return;
                }
                
                break;
              } else {
                throw new Error('Invalid response format');
              }
            } catch (error) {
              retryCount++;
              console.error('Error in performSearch:', error, error.response);
              if (error.response?.status === 403 || error.response?.data?.error === 'TOKEN_EXPIRED') {
                const newToken = await tokenService.refreshApiToken();
                if (!newToken) {
                  this.error = 'Authentication failed. Please log in again.';
                  this.allProducts = [];
                  console.error('UI error state set:', this.error);
                  return;
                }
                continue;
              }
              if (retryCount >= maxRetries) {
                this.error = error.response?.data?.message || 'Failed to search products. Please try again.';
                this.allProducts = [];
                console.error('UI error state set:', this.error);
                return;
              }
              await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
            }
          }
        } catch (error) {
          if (error.name === 'AbortError') {
            console.log('Search request aborted');
            return;
          }
          this.error = error.response?.data?.message || 'Failed to search products. Please try again.';
          this.allProducts = [];
          this.loadingMorePages = false;
          this.streamComplete = true;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Search error:', error);
          this.error = error.response?.data?.message || 'Failed to search products. Please try again.';
          this.allProducts = [];
        }
      } finally {
        if (this.currentSearchQuery === searchQueryAtStart) {
          this.initialLoading = false;
          this.loadingMorePages = false;
        }
      }
    },
    
    resetSearch(preservePageOptions = false) {
      // Preserve minPrice and maxPrice
      const minPrice = this.minPrice;
      const maxPrice = this.maxPrice;

      // Clear all search-related data except minPrice, maxPrice, and optionally page
      this.allProducts = [];
      this.initialLoading = true;
      this.loadingMorePages = false;
      this.currentPage = preservePageOptions && this.$route.query.page ? parseInt(this.$route.query.page) : 1;
      this.error = null;
      this.streamComplete = false;

      // Restore minPrice and maxPrice
      this.minPrice = minPrice;
      this.maxPrice = maxPrice;
    },
    
    handleImageError(e) {
      e.target.src = this.getDefaultProductImage();
    },
    
    handleSellerImageError(e) {
      e.target.src = this.getDefaultSellerImage();
    },
    
    goToProductDetail(product) {
      // Always stays inside Curator Eden — never follow the Goofish source URL.
      if (!productNavigation.openProduct(this.$router, product)) {
        console.warn('Unable to navigate to product detail: no item id on', product);
      }
    },
    
    async handleSort() {
      // Reset the search when sort option changes
      this.resetSearch();
      this.allProducts = [];
      this.initialLoading = true;
      
      try {
        const apiUrl = `/api/search/page?keyword=${this.searchQuery.trim()}&page=1&priceFilter=${this.sortOption}${this.minPrice ? `&minPrice=${this.minPrice}` : ''}${this.maxPrice ? `&maxPrice=${this.maxPrice}` : ''}`;
        
        const response = await tokenService.executeAuthenticatedRequest(async (token) => {
          return await this.$http.post(apiUrl, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        });
        
        if (response.data && Array.isArray(response.data.products)) {
          const processedProducts = response.data.products.map(product => ({
            ...product,
            text: product.text || 'No title',
            price: product.price || 'Price not available',
            image: this.sanitizeImageUrl(product.image),
            sellerName: product.sellerName || 'Seller',
            sellerImage: this.sanitizeImageUrl(product.sellerImage) || this.getDefaultSellerImage(),
            sellerReviews: product.sellerReviews || null,
            badge: product.badge || null,
            productLink: product.productLink || null
          }));
          
          this.initialLoading = false;
          this.allProducts = this.deduplicateProducts(processedProducts);
          this.currentPage = response.data.page;
          this.maxPages = response.data.totalPages;
          this.streamComplete = !response.data.hasMorePages;
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error in handleSort:', error);
        this.error = 'Failed to update sort order. Please try again.';
        this.initialLoading = false;
      }
    },

    sortProducts() {
      this.allProducts.sort((a, b) => {
        const priceA = parseFloat(a.price.replace(/[^0-9.-]+/g, ''));
        const priceB = parseFloat(b.price.replace(/[^0-9.-]+/g, ''));
        
        if (this.sortOption === 'desc') {
          return priceB - priceA; // High to Low
        } else if (this.sortOption === 'asc') {
          return priceA - priceB; // Low to High
        } else {
          // Default sorting - maintain original order
          return 0;
        }
      });
    },
    
    handlePriceFilter() {
      // Convert empty strings to null
      if (this.minPrice === '') this.minPrice = null;
      if (this.maxPrice === '') this.maxPrice = null;
      
      // Only validate if both values are set
      if (this.minPrice !== null && this.maxPrice !== null) {
        if (this.maxPrice < this.minPrice) {
          // Store the current input temporarily
          const tempMax = this.maxPrice;
          // Reset max to null to allow new input
          this.maxPrice = null;
          // Set timeout to allow new input
          setTimeout(() => {
            // If no new input was made, revert to min price
            if (this.maxPrice === null) {
              this.maxPrice = this.minPrice;
            }
          }, 3000);
        }
      }
    },

    // Add this new method for deduplication
    deduplicateProducts(products) {
      const uniqueProducts = new Map();
      products.forEach(product => {
        // Use product ID or productLink as unique identifier
        const key = product.id || product.productLink || product.text;
        if (!uniqueProducts.has(key)) {
          uniqueProducts.set(key, product);
        }
      });
      return Array.from(uniqueProducts.values());
    },

    // Add new method for sanitizing image URLs
    sanitizeImageUrl(url) {
      if (!url) return this.getDefaultProductImage();
      
      try {
        // Convert URL to HTTPS if it's HTTP
        if (url.startsWith('http:')) {
          url = 'https:' + url.substring(5);
        }
        
        // Validate URL
        new URL(url);
        return url;
      } catch (e) {
        console.warn('Invalid image URL:', url);
        return this.getDefaultProductImage();
      }
    },

    // Add method for default product image
    getDefaultProductImage() {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUwIiBoZWlnaHQ9IjQ1MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==';
    },

    // Add method for default seller image
    getDefaultSellerImage() {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAiIGhlaWdodD0iMzAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMTUiIGN5PSIxNSIgcj0iMTUiIGZpbGw9IiNmMGYwZjAiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjNjY2IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+UzwvdGV4dD48L3N2Zz4=';
    },
    async goToPage(page) {
      if (page < 1 || page > this.maxPages || page === this.currentPage) {
        return;
      }
      
      this.loadingMorePages = true;
      this.currentPage = page;
      this.pageInput = page;
      
      // Update URL to reflect new page
      try {
        if (Number(this.$route.query.page) !== page) {
          this.$router.replace({
            query: { ...this.$route.query, page }
          }).catch(err => {
            if (err.name !== 'NavigationDuplicated') console.error(err);
          });
        }
      } catch (e) {
        // ignore
      }

      // Ensure user sees top of the page after changing pages
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        // fallback
        window.scrollTo(0, 0);
      }
      
      try {
        const apiUrl = `/api/search/page?keyword=${this.searchQuery.trim()}&page=${page}&priceFilter=${this.sortOption}${this.minPrice ? `&minPrice=${this.minPrice}` : ''}${this.maxPrice ? `&maxPrice=${this.maxPrice}` : ''}`;
        
        let retryCount = 0;
        const maxRetries = 3;
        
        while (retryCount < maxRetries) {
          try {
            const response = await tokenService.executeAuthenticatedRequest(async (token) => {
              return await this.$http.post(apiUrl, {}, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
            });
            
            if (response.data && Array.isArray(response.data.products)) {
              const processedProducts = response.data.products.map(product => ({
                ...product,
                text: product.text || 'No title',
                price: product.price || 'Price not available',
                image: this.sanitizeImageUrl(product.image),
                sellerName: product.sellerName || 'Seller',
                sellerImage: this.sanitizeImageUrl(product.sellerImage) || this.getDefaultSellerImage(),
                sellerReviews: product.sellerReviews || null,
                badge: product.badge || null,
                productLink: product.productLink || null
              }));
              
              this.allProducts = this.deduplicateProducts(processedProducts);
              this.maxPages = response.data.totalPages;
              this.streamComplete = !response.data.hasMorePages;
              this.error = null; // clear error on success
              break; // Success, exit retry loop
            } else {
              throw new Error('Invalid response format');
            }
          } catch (error) {
            retryCount++;
            console.error('Error in goToPage:', error, error.response);
            if (error.response?.status === 403 || error.response?.data?.error === 'TOKEN_EXPIRED') {
              const newToken = await tokenService.refreshApiToken();
              if (!newToken) {
                this.error = 'Authentication failed. Please log in again.';
                this.allProducts = [];
                console.error('UI error state set:', this.error);
                return;
              }
              continue;
            }
            if (retryCount >= maxRetries) {
              this.error = error.response?.data?.message || 'Failed to load page. Please try again.';
              this.allProducts = [];
              console.error('UI error state set:', this.error);
              return;
            }
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 1000));
          }
        }
      } finally {
        this.loadingMorePages = false;
      }
    },
    async applyPriceFilter() {
      console.log('Apply filter button clicked'); // Log to verify button click
      if (this.minPrice === '' && this.maxPrice === '') {
        console.log('No price filter applied'); // Log if no price filter is applied
        return;
      }

      this.resetSearch();
      this.allProducts = [];
      this.initialLoading = true;

      try {
        console.log('minPrice:', this.minPrice, 'maxPrice:', this.maxPrice); // Debug log for minPrice and maxPrice
        const apiUrl = `/api/search/page?keyword=${this.searchQuery.trim()}&page=${this.currentPage}&priceFilter=${this.sortOption}${this.minPrice ? `&minPrice=${this.minPrice}` : ''}${this.maxPrice ? `&maxPrice=${this.maxPrice}` : ''}`;
        console.log('API URL:', apiUrl); // Debug log to check API URL
        
        const response = await tokenService.executeAuthenticatedRequest(async (token) => {
          return await this.$http.post(apiUrl, {}, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
        });
        
        if (response.data && Array.isArray(response.data.products)) {
          const processedProducts = response.data.products.map(product => ({
            ...product,
            text: product.text || 'No title',
            price: product.price || 'Price not available',
            image: this.sanitizeImageUrl(product.image),
            sellerName: product.sellerName || 'Seller',
            sellerImage: this.sanitizeImageUrl(product.sellerImage) || this.getDefaultSellerImage(),
            sellerReviews: product.sellerReviews || null,
            badge: product.badge || null,
            productLink: product.productLink || null
          }));
          
          this.initialLoading = false;
          this.allProducts = this.deduplicateProducts(processedProducts);
          this.currentPage = response.data.page;
          this.maxPages = response.data.totalPages;
          this.streamComplete = !response.data.hasMorePages;
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error applying price filter:', error);
        this.error = 'Failed to apply price filter. Please try again.';
        this.initialLoading = false;
      }
    },
    logPriceInput() {
      console.log('minPrice input:', this.minPrice);
      console.log('maxPrice input:', this.maxPrice);
    },
  }
}
</script>

<style scoped>
.product-card {
  position: relative;
}

.favourite-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.favourite-heart {
  font-size: 18px;
}

.favourite-heart.is-active {
  color: #ff4444;
}

.favourite-heart.popping {
  animation: favPop 200ms ease-out;
}

@keyframes favPop {
  0% { transform: scale(0.85); }
  60% { transform: scale(1.15); }
  100% { transform: scale(1.0); }
}
</style>

<style scoped>
.search {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

h1 {
  text-align: center;
  color: #333;
  font-size: 1.5rem;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  color: #721c24;
  background: #f8d7da;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  text-align: center;
}

.retry-button {
  margin-top: 15px;
  padding: 10px 20px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #c82333;
}

.no-results {
  text-align: center;
  padding: 60px;
  color: #666;
  font-size: 1.2rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.products {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

.product-card {
  overflow: hidden;
  background: white;
  animation: fadeIn 0.3s ease-in;
  /*display: flex;*/
  flex-direction: column;
  height: 410px;
  position: relative;
  cursor: pointer;
}

.product-image {
  position: relative;
  width: 100%;
  height: 15rem;
  background: #f8f8f8;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

.product-details {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.product-price-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.product-price-row .favourite-btn {
  margin-left: auto;
}

.product-text-title {
  margin: 0;
  font-size: 0.9rem;
  color: #333;
  font-weight: 500;
  line-height: 1.3;
  height: 2.8em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.product-price {
  font-size: 1.1rem;
  color: #e44d26;
  font-weight: bold;
  margin: 0;
}

.seller-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
}

.seller-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.seller-image {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.seller-name {
  color: #666;
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.reviews-count {
  font-size: 0.8rem;
  color: #888;
  white-space: nowrap;
  margin-left: 32px; /* Same as seller image width + gap */
}

.loading-more {
  text-align: center;
  padding: 20px;
  color: #666;
}

.loading-more .spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 10px;
  border-width: 4px;
}

.end-message {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}

.scroll-sentinel {
  height: 1px;
  margin-bottom: 100px;
}

/* Add lazy loading animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}


.product-specs {
  color: #666;
  font-size: 0.8rem;
  margin: 3px 0;
  line-height: 1.3;
  height: auto;
  max-height: 3.9em;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.product-badge {
  position: absolute;
  bottom: 5px;
  left: 5px;
  font-size: 0.8rem;
  color: #555;
  padding: 2px 6px;
  background-color: rgba(255, 255, 255, 0.85);
  border-radius: 4px;
  z-index: 1;
  margin: 0;
}

.filters-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 8px;
}

.sort-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  font-size: 0.9rem;
  /*
  color: #333;
  cursor: pointer;
  min-width: 180px;*/
}

.sort-container {
  display: flex;
  justify-content: flex-end;
}

.sort-select:hover {
  border-color: #999;
}

.sort-select:focus {
  outline: none;
  border-color: #666;
}

/*.price-filter {
  display: flex;
  align-items: center;
  gap: 15px;
}*/

.price-inputs {
  display: flex;
  gap: 10px;
}

.price-banner {
  width: auto;
  max-width: 100%;
  margin: 16px 8px; /* match .filters-container / .products side padding */
  padding: 12px 18px;
  background: #fff8e1;
  border-left: 4px solid #f0c040;
  color: #333;
  font-size: 0.95rem;
  border-radius: 6px;
  box-sizing: border-box;
  overflow: hidden;
  word-wrap: break-word;
}

.price-input-group {
  display: flex;
  align-items: center;
  gap: 5px;
}
/*
.price-input-group label {
  color: #666;
  white-space: nowrap;
}
*/
.price-input {
  width: 100px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.price-input:focus {
  outline: none;
  border-color: #666;
}

.price-input::-webkit-inner-spin-button,
.price-input::-webkit-outer-spin-button {
  opacity: 1;
}

@media (max-width: 768px) {
  .search {
    padding: 15px;
  }

  h1 {
    font-size: 1.2rem;
    margin-bottom: 15px;
  }

  .filters-container {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
    margin-bottom: 15px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
  }
  
  .sort-container {
    width: 100%;
    justify-content: stretch;
    margin-bottom: 0;
  }
  
  .sort-select {
    width: 100%;
    padding: 12px;
    font-size: 16px;
  }
  
  .price-filter {
    width: 100%;
  }
  
  .price-inputs {
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: 10px;
  }

  .price-banner {
    max-width: 100%;
    margin: 12px 0;
    padding: 10px 12px;
    font-size: 0.95rem;
    box-sizing: border-box;
  }
  
  .price-input-group {
    width: 50%;
    display: flex;
    align-items: center;
  }
  
  .price-input {
    width: 100%;
    padding: 12px;
    font-size: 16px;
  }

  .pagination {
    margin-top: 15px;
  }

  .pagination-btn {
    padding: 8px 15px;
    font-size: 14px;
  }

  .page-numbers {
    margin: 0 5px;
  }

  .page-number {
    padding: 4px 8px;
    font-size: 14px;
    margin: 0 1px;
  }
}

/* Remove all previous media queries for larger screens and start fresh */
@media (min-width: 760px) {
  .products {
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }
  .product-card {
    height: auto;
  }
  .product-image {
    height: 18rem;
  }
}

@media (max-width: 760px) {
  .filters-container {
    padding: 12px;
    gap: 12px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .sort-select {
    font-size: 14px;
    padding: 10px;
  }

  .price-filter {
    gap: 8px;
  }

  .price-input-group label {
    font-size: 14px;
  }

  .price-input {
    font-size: 14px;
    padding: 10px;
  }

  .products {
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
    padding: 0 5px;
  }

  .price-banner {
    margin-left: 5px;
    margin-right: 5px;
  }
  .product-card {
    height: auto;
  }
  .product-image {
    height: 14rem;
  }
  .product-details {
    padding: 8px;
  }
  .product-text-title {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .search {
    padding: 10px;
  }

  h1 {
    font-size: 1.1rem;
    padding: 0 10px;
  }

  .filters-container {
    padding: 12px;
    margin: 0 5px 15px 5px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .price-inputs {
    gap: 8px;
  }

  .price-input-group {
    width: 50%;
  }

  .price-input {
    padding: 10px;
  }

  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 10px;
  }

  .pagination-btn {
    padding: 6px 12px;
    font-size: 13px;
  }

  .page-numbers {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 3px;
    margin: 5px 0;
  }

  .page-number {
    padding: 4px 6px;
    font-size: 13px;
    min-width: 28px;
  }
}

@media (max-width: 470px) {
  .filters-container {
    padding: 10px;
    gap: 10px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .sort-select {
    font-size: 13px;
    padding: 8px;
  }

  .price-filter {
    gap: 6px;
  }

  .price-banner {
    display: block;
    font-size: 0.9rem;
    padding: 8px;
    margin: 8px 4px;
    box-sizing: border-box;
  }

  .price-inputs {
    gap: 8px;
  }

  .price-input-group label {
    display: none; /* Hide labels only at 380px and below */
  }

  .price-input-group {
    width: 50%;
  }

  .price-input {
    font-size: 13px;
    padding: 8px;
  }

  .products {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 0 4px;
  }
  .product-card {
    height: auto;
  }
  .product-image {
    height: 11rem;
  }
  .product-details {
    padding: 8px;
  }
  .product-text-title {
    font-size: 0.85rem;
    height: 2.7em;
    /*height: 2.4em;*/
  }
  .product-price {
    font-size: 1rem;
  }
  .seller-details {
    padding-top: 6px;
  }
  .seller-image {
    width: 20px;
    height: 20px;
  }
  .seller-name {
    font-size: 0.8rem;
  }
  .reviews-count {
    font-size: 0.75rem;
    margin-left: 28px;
  }
}

@media (max-width: 380px) {
  .filters-container {
    padding: 8px;
    gap: 8px;
  }

  .sort-container {
    margin-bottom: -8px;
  }

  .sort-select {
    font-size: 12px;
    padding: 8px;
  }

  .price-input-group label {
    display: none; /* Hide labels only at 380px and below */
  }

  .price-input {
    font-size: 12px;
    padding: 8px;
  }

  .product-card {
    height: 310px;
  }
  .product-image {
    height: 11rem;
  }
  .product-details {
    padding: 6px;
  }
  .product-text-title {
    font-size: 0.8rem;
  }

  .pagination {
    margin-top: 8px;
  }

  .pagination-btn {
    padding: 5px 10px;
    font-size: 12px;
  }

  .page-number {
    padding: 3px 5px;
    font-size: 12px;
    min-width: 24px;
  }
}

.wait-time {
  margin-top: 10px;
  color: #888;
  font-size: 0.9rem;
}

.cache-indicator {
  text-align: center;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 20px;
}

.fresh {
  color: #28a745;
}

.stale {
  color: #dc3545;
}

.refresh-button {
  margin-top: 10px;
  padding: 10px 20px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.refresh-button:hover {
  background: #c82333;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  gap: 10px;
  flex-wrap: wrap;
  padding: 0 10px;
}

.pagination-btn {
  padding: 10px 20px;
  background: #000000;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.pagination-btn:disabled {
  background: #666666;
  cursor: not-allowed;
}

.pagination-btn:hover:not(:disabled) {
  background: #333333;
}

.page-input {
  width: 60px;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 1rem;
}

.page-input:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 5px rgba(52, 152, 219, 0.5);
}

.apply-btn {
  padding: 8px 16px;
  background: #000000;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.apply-btn:hover {
  background: #333333;
}

.full-page-loader {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.pagination-btn.go-btn {
  background: #ffffff;
  color: #000000;
  border: 1px solid #000000;
}

.pagination-btn.go-btn:hover:not(:disabled) {
  background: #f0f0f0;
}

.pagination-btn.go-btn:disabled {
  background: #f0f0f0;
  color: #666666;
  border-color: #666666;
}

@media (max-width: 768px) {
  .pagination {
    flex-wrap: nowrap;
    gap: 4px;
  }

  .pagination-btn {
    padding: 6px 6px;
    font-size: 12px;
    white-space: nowrap;
    min-width: 0;
  }

  .page-input {
    width: 40px;
    padding: 6px 4px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .pagination {
    gap: 4px;
    margin-top: 15px;
  }

  .pagination-btn {
    padding: 5px 6px;
    font-size: 12px;
    min-width: 0;
  }

  .page-input {
    width: 40px;
    padding: 5px 4px;
    font-size: 12px;
  }
}

@media (max-width: 380px) {
  .pagination {
    gap: 3px;
  }

  .pagination-btn {
    padding: 4px 6px;
    font-size: 11px;
    min-width: 0;
  }

  .page-input {
    width: 35px;
    padding: 4px;
    font-size: 11px;
  }
}

</style> 